/* ===========================================================================
   assets/va-artcheck.js  —  shared customer-artwork gate
   ---------------------------------------------------------------------------
   Phase 3.4 of the v3 background-upload plan. Every rule here is LIFTED from
   assets/va-upload.js (live copy read 2026-09-04, updated_at 2026-09-02) so the
   two surfaces refuse and admit the same files. This file is DELIBERATE
   duplication: sections/va-builder-v3.liquid is the only consumer for now and
   va-upload.js is NOT cut over in the same deploy. When va-upload.js is finally
   pointed at this module, delete its private sniff/blankScan/detailScan/validate
   rather than letting the pair drift — this codebase already carries three
   "lives in TWO copies" traps.

   WHAT A BACKGROUND IS
   --------------------
   The customer's image replaces the FIRST <image> href inside a catalog
   template. That tag is cover-fitted by the browser
   (preserveAspectRatio="xMidYMid slice") and by the press
   (label_factory._fit_cover) — byte-equivalent operations — so a background is
   EXPECTED to be trimmed. That is why the +/-10% label-shape band and the
   cross-die check from va-upload.js do NOT apply here: the die comes from the
   chosen template, not from the file. What protects print quality instead is
   the DPI floor measured on the COVER-CROPPED result (gate 3) plus a trim ack
   that names the edges (gate 4).

   THE IMAGE BOX IS NOT THE VIEWBOX, AND IT IS NOT ONE SHAPE
   ---------------------------------------------------------
   3 mL is uniform (376/376 templates): x=0 y=0 w=44.958 h=19.05.
   10 mL is NOT. Measured across all 376 live 10 mL templates:
     x=0     y=0     w=63.5 h=25.4  -> 154 (beta_centered_*, 2.500)
     x=-0.25 y=-0.8  w=64   h=27    -> 145 (beta_banner_*,   2.370)  <- bleeds
     x=0     y=0     w=65   h=26    ->  77 (beta_ocean_*,    2.500)
   Hardcoding any ONE of those is wrong for 61% of 10 mL designs, and it skews
   the trim ack, the cropped-DPI floor and the zone-luma sample. So the CALLER
   measures the box off the template it actually loaded and passes it as
   opts.box; the DIES table below is only the fallback for a template whose SVG
   has not been fetched yet. Same rule _swap_bg_image follows server-side: read
   the tag's own width/height, never re-derive it from the die.

   API
   ---
   window.vaArtCheck.validate(file, {die:'3ml'|'10ml', mode:'background'|'label',
                                     box:{w,h}   // the chosen template's own
                                                 // <image> box, in mm
                                    })
     -> Promise<{ ok, hard[], warns[], acks[], dims:{w,h}, cropDpi, dataUrl }>
        hard / warns : [{ id, text }]        acks : [{ id, label, sub }]
        Every ack is MANDATORY — the caller must render a ticked checkbox per
        entry before it may proceed. `ok` is hard.length === 0 and says nothing
        about the acks.
        dataUrl is a preview-sized (never cropped) data: URL, present only when
        ok. Scaling preserves the aspect ratio, so the browser's slice-crop of
        the preview is identical to the press's crop of the original.

   window.vaArtCheck.scan(file)   -> Promise, fails OPEN, throws err.vaBlock
   window.vaArtCheck.upload(file, die) -> Promise<{id, type, ext}>

   COPY RULE (peptide-marketing-liability): every string in this file talks about
   the label, the image, the print or the material. Never a compound, a dose, or
   a use.
   =========================================================================== */
(function () {
  'use strict';
  if (window.vaArtCheck) return;

  /* ---------------------------------------------------------------------
     constants — lifted verbatim from va-upload.js:75-76, :1007-1019
     200 DPI HARD FLOOR (owner spec 2026-07-17, was 600 then 300). 600+ reads
     "sharp"; 200-599 passes on a soft ack; under 200 is a hard reject. Locked
     in code on purpose — cfg.dpiMin / cfg.dpiPass are ignored on both surfaces.
     --------------------------------------------------------------------- */
  var DPI_PASS = 600, DPI_MIN = 200;
  var MAX_MB = 20, MAX_BYTES = MAX_MB * 1024 * 1024;
  var MIN_BYTES = 10 * 1024;
  var WARN_BYTES = 15 * 1024 * 1024;
  /* 60 MP, NOT 100: core/label_renderer.py MAX_ARTWORK_PIXELS is 64 MP and
     _open_artwork_image RAISES above it, so a 64-100 MP file (a plain
     9000x9000 export is 81 MP) used to pass every gate, upload, force human
     review — and then fail the background swap on EVERY render, silently
     printing the catalog background. Lowest ceiling wins and it is enforced
     here first; cloud/vialart-design-worker.js mirrors this number on the
     ?bg=1 lane so the backstop stays equal, never stricter. */
  var MAX_MP = 60, WARN_MP = 40;
  /* gate 4(a): the mis-filed-image sanity band. Outside 0.5:1 .. 6:1 nothing
     sane survives a cover-crop to a label (a 1:4 portrait loses 85% of itself,
     a panorama loses the same off the sides). */
  var AR_MIN = 0.5, AR_MAX = 6;
  /* fine-detail scan: non-blocking since 2026-07-17 (va-upload.js:1104-1119) */
  var DETAIL_LOSS = 0.75;
  /* gate 7 legibility: mean luminance in a reserved text zone, 0..255. Default
     ink is WHITE (the beta templates outline their text in white), so a very
     LIGHT background is the hazard. */
  var LUM_LIGHT = 165, LUM_DARK = 90;

  var WORKER_BASE = 'https://vialart-design.vialart.workers.dev';
  var SCAN_BASE   = 'https://vialart-scan.vialart.workers.dev';

  /* FALLBACK art box per die, in mm — used only when the caller could not
     measure the chosen template (its SVG has not loaded yet). `bg` is the most
     common 10 mL geometry, not the only one; see the header. `label_box` is
     the viewBox and is exact, used only by mode:'label'. */
  var DIES = {
    '3ml':  { key: '3ml',  label: '3 mL',  bg: { w: 44.958, h: 19.05 }, label_box: { w: 44.958, h: 19.05 } },
    '10ml': { key: '10ml', label: '10 mL', bg: { w: 64.0,   h: 27.0  }, label_box: { w: 63.5,   h: 25.4  } }
  };
  function dieDef(k) { return DIES[k === '10ml' ? '10ml' : '3ml']; }
  function boxFor(k, mode, box) {
    /* a measured box always wins: it came off the template the customer picked */
    if (mode !== 'label' && box && +box.w > 0 && +box.h > 0) {
      return { w: +box.w, h: +box.h };
    }
    var d = dieDef(k);
    return (mode === 'label') ? d.label_box : d.bg;
  }

  /* Reserved text zones, as FRACTIONS of the canvas. Ported from
     core/label_factory.py:49 RESERVED_ZONES (mm on the 44.958 x 19.05 canvas)
     and divided through, so the same relative geometry applies to both dies —
     the 10 mL layouts are the 3 mL layouts scaled up. */
  var CW = 44.958, CH = 19.05;
  var ZONES = [
    { key: 'title',    x: 6.5 / CW,  y: 5.40 / CH,  w: 32.0 / CW, h: 4.70 / CH },
    { key: 'dose',     x: 14.8 / CW, y: 9.70 / CH,  w: 15.4 / CW, h: 3.60 / CH },
    { key: 'research', x: 11.8 / CW, y: 1.95 / CH,  w: 21.4 / CW, h: 1.90 / CH },
    { key: 'lot',      x: 2.40 / CW, y: 14.85 / CH, w: 10.2 / CW, h: 1.75 / CH },
    { key: 'exp',      x: 32.2 / CW, y: 14.85 / CH, w: 10.6 / CW, h: 1.75 / CH },
    { key: 'footer',   x: 13.0 / CW, y: 16.80 / CH, w: 19.0 / CW, h: 1.55 / CH }
  ];

  /* ---------------------------------------------------------------------
     small helpers
     --------------------------------------------------------------------- */
  function fmtMB(b) { var v = b / 1048576; return v < 10 ? v.toFixed(1) : String(Math.round(v)); }
  function cust() { try { return String(window.VA_CUSTOMER || '').trim(); } catch (e) { return ''; } }
  function workerBase() {
    try { return String(window.VA_UPLOAD_URL || WORKER_BASE).replace(/\/upload\/?$/, ''); }
    catch (e) { return WORKER_BASE; }
  }

  /* magic bytes — va-upload.js:871. Extension and MIME are both forgeable, so
     the real signature is the third of three signals that must agree. */
  function sniff(buf) {
    var u = new Uint8Array(buf);
    if (u[0] === 0x89 && u[1] === 0x50 && u[2] === 0x4E && u[3] === 0x47) return 'png';
    if (u[0] === 0xFF && u[1] === 0xD8 && u[2] === 0xFF) return 'jpg';
    if (u[0] === 0x25 && u[1] === 0x50 && u[2] === 0x44 && u[3] === 0x46) return 'pdf';
    if (u[0] === 0x47 && u[1] === 0x49 && u[2] === 0x46) return 'gif';
    if (u[0] === 0x52 && u[1] === 0x49 && u[2] === 0x46 && u[3] === 0x46
        && u[8] === 0x57 && u[9] === 0x45 && u[10] === 0x42 && u[11] === 0x50) return 'webp';
    var head = '', i;
    for (i = 0; i < Math.min(u.length, 200); i++) head += String.fromCharCode(u[i]);
    head = head.toLowerCase();
    if (head.indexOf('<svg') >= 0 || (head.indexOf('<?xml') >= 0 && head.indexOf('svg') >= 0)) return 'svg';
    return 'other';
  }

  /* animated PNG — va-upload.js:916 */
  function isAnimatedPng(file) {
    return file.slice(0, 131072).arrayBuffer().then(function (buf) {
      var u = new Uint8Array(buf), s = '', i;
      for (i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
      var actl = s.indexOf('acTL'), idat = s.indexOf('IDAT');
      return actl >= 0 && (idat < 0 || actl < idat);
    }).catch(function () { return false; });
  }

  /* pixel dimensions — va-upload.js:884 */
  function decodeDims(file) {
    return new Promise(function (resolve, reject) {
      function fallback() {
        var url = URL.createObjectURL(file), im = new Image();
        im.onload = function () { var d = { w: im.naturalWidth, h: im.naturalHeight }; URL.revokeObjectURL(url); resolve(d); };
        im.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
        im.src = url;
      }
      if (window.createImageBitmap) {
        createImageBitmap(file).then(function (bm) {
          var d = { w: bm.width, h: bm.height };
          if (bm.close) bm.close();
          resolve(d);
        }).catch(fallback);
      } else { fallback(); }
    });
  }

  /* blank / transparent — va-upload.js:899 */
  function blankScan(file) {
    return new Promise(function (resolve) {
      if (!window.createImageBitmap) { resolve(null); return; }
      createImageBitmap(file).then(function (bm) {
        try {
          var c = document.createElement('canvas'); c.width = 64; c.height = 64;
          var ctx = c.getContext('2d'); ctx.drawImage(bm, 0, 0, 64, 64);
          if (bm.close) bm.close();
          var d = ctx.getImageData(0, 0, 64, 64).data, n = 64 * 64;
          var allAlpha0 = true, anyAlphaLt = false, sum = 0, vals = [], i, j;
          for (i = 0; i < d.length; i += 4) {
            var a = d[i + 3];
            if (a !== 0) allAlpha0 = false;
            if (a < 255) anyAlphaLt = true;
            var lum = (d[i] + d[i + 1] + d[i + 2]) / 3;
            sum += lum; vals.push(lum);
          }
          var mean = sum / n, vc = 0;
          for (j = 0; j < vals.length; j++) { var dv = vals[j] - mean; vc += dv * dv; }
          resolve({ allAlpha0: allAlpha0, anyAlphaLt: anyAlphaLt, std: Math.sqrt(vc / n) });
        } catch (e) { resolve(null); }
      }).catch(function () { resolve(null); });
    });
  }

  /* print-legibility (shrink) scan — va-upload.js:929, verbatim.
     Renders the art at its TRUE printed size (placedMm wide @ 203-DPI thermal)
     and measures the fraction of foreground ink living in <=1px strokes. */
  function detailScan(file, placedMm) {
    return new Promise(function (resolve) {
      function calc(src, w, h) {
        try {
          var pw = Math.max(8, Math.round(placedMm / 25.4 * 203)), ph = Math.max(4, Math.round(h * pw / w));
          var c = document.createElement('canvas'); c.width = pw; c.height = ph;
          var cx = c.getContext('2d', { willReadFrequently: true });
          cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high';
          cx.drawImage(src, 0, 0, pw, ph);
          var d = cx.getImageData(0, 0, pw, ph).data, n = pw * ph, i;
          var gray = new Uint8Array(n), hist = new Uint32Array(256);
          for (i = 0; i < n; i++) {
            var g = (0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2]) | 0;
            gray[i] = g; hist[g]++;
          }
          var bg = 0, bc = 0;
          for (i = 0; i < 256; i++) if (hist[i] > bc) { bc = hist[i]; bg = i; }
          var fg = new Uint8Array(n), fgc = 0;
          for (i = 0; i < n; i++) if (Math.abs(gray[i] - bg) > 40) { fg[i] = 1; fgc++; }
          if (fgc < 8) { resolve(0); return; }
          var er = 0, x, y, dx, dy, keep;
          for (y = 0; y < ph; y++) for (x = 0; x < pw; x++) {
            if (!fg[y * pw + x]) continue;
            keep = 1;
            for (dy = -1; dy <= 1 && keep; dy++) for (dx = -1; dx <= 1; dx++) {
              var ny = y + dy, nx = x + dx;
              if (ny < 0 || nx < 0 || ny >= ph || nx >= pw || !fg[ny * pw + nx]) { keep = 0; break; }
            }
            if (keep) er++;
          }
          resolve(1 - er / fgc);
        } catch (e) { resolve(0); }
      }
      if (window.createImageBitmap) {
        createImageBitmap(file).then(function (bm) {
          var w = bm.width, h = bm.height; calc(bm, w, h); if (bm.close) bm.close();
        }).catch(function () { resolve(0); });
      } else {
        var url = URL.createObjectURL(file), im = new Image();
        im.onload = function () { URL.revokeObjectURL(url); calc(im, im.naturalWidth, im.naturalHeight); };
        im.onerror = function () { URL.revokeObjectURL(url); resolve(0); };
        im.src = url;
      }
    });
  }

  /* gate 7 — legibility under the reserved text zones (NEW, background only).
     Draws the COVER-CROPPED image at 360px wide and averages luminance inside
     each zone. Non-blocking, and it NEVER recolours anything: recolouring would
     need the accent/mode re-bake and would break preview == print. */
  function zoneLuma(file, boxRatio) {
    return new Promise(function (resolve) {
      function calc(src, w, h) {
        try {
          var cw = 360, ch = Math.max(8, Math.round(cw / boxRatio));
          var c = document.createElement('canvas'); c.width = cw; c.height = ch;
          var cx = c.getContext('2d', { willReadFrequently: true });
          /* cover-crop: exactly what preserveAspectRatio="xMidYMid slice" and
             label_factory._fit_cover both do */
          var ar = w / h, sx = 0, sy = 0, sw = w, sh = h;
          if (ar > boxRatio) { sw = h * boxRatio; sx = (w - sw) / 2; }
          else { sh = w / boxRatio; sy = (h - sh) / 2; }
          cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, cw, ch);
          cx.drawImage(src, sx, sy, sw, sh, 0, 0, cw, ch);
          var d = cx.getImageData(0, 0, cw, ch).data;
          var out = [], zi;
          for (zi = 0; zi < ZONES.length; zi++) {
            var z = ZONES[zi];
            var x0 = Math.max(0, Math.round(z.x * cw)), y0 = Math.max(0, Math.round(z.y * ch));
            var x1 = Math.min(cw, Math.round((z.x + z.w) * cw)), y1 = Math.min(ch, Math.round((z.y + z.h) * ch));
            if (x1 <= x0 || y1 <= y0) continue;
            var sum = 0, n = 0, px, py;
            for (py = y0; py < y1; py++) for (px = x0; px < x1; px++) {
              var i = (py * cw + px) * 4;
              sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
              n++;
            }
            if (n) out.push({ key: z.key, lum: sum / n });
          }
          resolve(out);
        } catch (e) { resolve([]); }
      }
      if (window.createImageBitmap) {
        createImageBitmap(file).then(function (bm) {
          var w = bm.width, h = bm.height; calc(bm, w, h); if (bm.close) bm.close();
        }).catch(function () { resolve([]); });
      } else {
        var url = URL.createObjectURL(file), im = new Image();
        im.onload = function () { URL.revokeObjectURL(url); calc(im, im.naturalWidth, im.naturalHeight); };
        im.onerror = function () { URL.revokeObjectURL(url); resolve([]); };
        im.src = url;
      }
    });
  }

  /* preview data URL. NOT cropped — scaling only, so the browser's slice-crop of
     this preview equals the press's crop of the original held in R2. A 20 MB
     original base64s to ~27 MB of string sitting inside innerHTML, which is what
     the long-edge cap avoids. */
  var PREVIEW_EDGE = 1600;
  function previewUrl(file, dims) {
    return new Promise(function (resolve) {
      var longEdge = Math.max(dims.w, dims.h);
      function raw() {
        var rd = new FileReader();
        rd.onload = function () { resolve(String(rd.result || '')); };
        rd.onerror = function () { resolve(''); };
        rd.readAsDataURL(file);
      }
      if (longEdge <= PREVIEW_EDGE && file.size <= 1.5 * 1024 * 1024) { raw(); return; }
      function calc(src, w, h) {
        try {
          var k = PREVIEW_EDGE / Math.max(w, h);
          if (k > 1) k = 1;
          var cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
          var c = document.createElement('canvas'); c.width = cw; c.height = ch;
          var cx = c.getContext('2d');
          cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high';
          cx.drawImage(src, 0, 0, cw, ch);
          /* PNG keeps its alpha. mode:'label' only — transparency is a warned
             hazard there (bare foil) that the customer has to be able to SEE in
             the proof. On mode:'background' gate 5 hard-rejects alpha before
             this line is ever reached, so a background preview is always opaque
             and therefore always agrees with the press. */
          var isPng = /png/i.test(file.type || '') || /\.png$/i.test(file.name || '');
          resolve(c.toDataURL(isPng ? 'image/png' : 'image/jpeg', isPng ? undefined : 0.85));
        } catch (e) { raw(); }
      }
      if (window.createImageBitmap) {
        createImageBitmap(file).then(function (bm) {
          var w = bm.width, h = bm.height; calc(bm, w, h); if (bm.close) bm.close();
        }).catch(raw);
      } else {
        var url = URL.createObjectURL(file), im = new Image();
        im.onload = function () { URL.revokeObjectURL(url); calc(im, im.naturalWidth, im.naturalHeight); };
        im.onerror = function () { URL.revokeObjectURL(url); raw(); };
        im.src = url;
      }
    });
  }

  /* ---------------------------------------------------------------------
     gate 9 — rejection audit. va-upload.js:373-392, plus surface=bg so Label
     Farm can tell a background refusal from a full-label one. Once per File
     object; fire-and-forget; a non-image or oversize attempt sends a tiny JSON
     meta ping instead of the bytes.
     --------------------------------------------------------------------- */
  function auditReject(file, reason, dpi, die) {
    try {
      if (!file || file.__vaAud) return;
      file.__vaAud = 1;
      var base = workerBase(); if (!base) return;
      var qs = '?reason=' + encodeURIComponent(String(reason || 'rejected').slice(0, 170))
             + '&fn=' + encodeURIComponent(String(file.name || '').slice(0, 70))
             + (dpi != null ? '&dpi=' + encodeURIComponent(dpi) : '')
             + '&size=' + encodeURIComponent(die || '3ml')
             + '&surface=bg'
             + (cust() ? '&cust=' + encodeURIComponent(cust().slice(0, 120)) : '');
      var isImg = /^image\/(png|jpe?g)$/i.test(file.type || '') && file.size <= 25 * 1024 * 1024;
      fetch(base + '/upload-audit' + qs, {
        method: 'POST',
        headers: { 'Content-Type': isImg ? file.type : 'application/json' },
        body: isImg ? file : JSON.stringify({ name: file.name || '', size: file.size || 0, type: file.type || '' })
      }).catch(function () {});
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------
     validate()
     --------------------------------------------------------------------- */
  function validate(file, opts) {
    opts = opts || {};
    var die = (opts.die === '10ml') ? '10ml' : '3ml';
    var mode = (opts.mode === 'label') ? 'label' : 'background';
    var box = boxFor(die, mode, opts.box);
    var BOX_W_IN = box.w / 25.4, BOX_H_IN = box.h / 25.4;
    var BOX_AR = box.w / box.h;
    /* white ink is the default: the beta templates outline their text in white,
       so a very LIGHT photo is what hurts. A caller that knows the template's
       baked ink may pass its 0..255 luminance. */
    var inkLum = (opts.inkLum == null) ? 255 : +opts.inkLum;

    var hard = [], warns = [], acks = [];
    var dims = null, cropDpi = null;

    function H(id, text) { hard.push({ id: id, text: text }); }
    function W(id, text) { warns.push({ id: id, text: text }); }
    function A(id, label, sub) { acks.push({ id: id, label: label, sub: sub || '' }); }

    function out(dataUrl) {
      var res = {
        ok: hard.length === 0,
        hard: hard, warns: warns, acks: acks,
        dims: dims || { w: 0, h: 0 },
        cropDpi: cropDpi,
        dataUrl: dataUrl || '',
        die: die, mode: mode,
        name: String(file && file.name || '')
      };
      if (!res.ok) auditReject(file, hard[hard.length - 1].text, cropDpi, die);
      return res;
    }
    function badType() {
      H('badtype', 'Unsupported file type. Upload your background as a PNG, JPG or JPEG '
        + '— those are the formats a browser displays exactly as we print them.');
      return out('');
    }

    if (!file) { H('nofile', 'No file was selected.'); return Promise.resolve(out('')); }

    return file.slice(0, 200).arrayBuffer().then(function (head) {
      var ty = sniff(head);

      /* ---- GATE 1: STRICT FORMAT. Three signals must ALL agree and must agree
         WITH EACH OTHER: filename extension, browser MIME, real magic bytes.
         WEBP / PDF / SVG / TIFF / HEIC / PSD / AI / EPS / GIF / BMP / AVIF and
         anything unknown are refused. (va-upload.js:983-1005) */
      var _name = String(file.name || '').toLowerCase();
      var _extPng = /\.png$/.test(_name), _extJpg = /\.jpe?g$/.test(_name);
      var _mime = String(file.type || '').toLowerCase();
      var _mimeOkP = (_mime === 'image/png' || _mime === '');
      var _mimeOkJ = (_mime === 'image/jpeg' || _mime === 'image/jpg' || _mime === '');
      var _agree = (_extPng && ty === 'png' && _mimeOkP) || (_extJpg && ty === 'jpg' && _mimeOkJ);
      if (!_agree) return badType();

      /* ---- GATE 2: FILE SIZE (va-upload.js:1007-1019) */
      if (file.size > MAX_BYTES) {
        H('toobig', 'This file is too large (' + fmtMB(file.size) + ' MB). Please keep it under ' + MAX_MB + ' MB.');
        return out('');
      }
      if (file.size < MIN_BYTES) {
        H('toosmall', 'This file is very small (' + (file.size / 1024).toFixed(1)
          + ' KB) and is likely too low-detail to print. Please upload a higher-quality image.');
        return out('');
      }
      if (file.size > WARN_BYTES) {
        W('bigfile', 'Large file (' + fmtMB(file.size) + ' MB) — the upload may take a moment.');
        A('bigfile', 'This is a large file and may take a moment to upload.');
      }

      var animChk = (ty === 'png') ? isAnimatedPng(file) : Promise.resolve(false);
      return animChk.then(function (anim) {
        if (anim) {
          H('animated', "Animated images aren't supported. Please upload a single still frame.");
          return out('');
        }
        return decodeDims(file).catch(function () { return null; }).then(function (dim) {
          if (!dim || !dim.w || !dim.h) return badType();   /* will not decode = cannot be a proof */
          dims = { w: dim.w, h: dim.h };
          var w = dim.w, h = dim.h, mp = (w * h) / 1e6;
          if (mp > MAX_MP) {
            H('hugemp', 'This image is enormous (' + mp.toFixed(0) + ' megapixels). Please resize it under ' + MAX_MP + ' MP.');
            return out('');
          }
          if (mp > WARN_MP) {
            W('bigmp', 'Very large image (' + mp.toFixed(0) + ' MP) — it will work but may be slow to process.');
            A('bigmp', 'This is a very large image and may be slow to process.');
          }

          /* ---- GATE 4(a): RATIO SANITY. Nothing outside 0.5:1 .. 6:1 survives a
             cover-crop to a label shape, so it is a mis-filed image, not a
             near-miss. The +/-10% label-shape band and the cross-die check from
             va-upload.js deliberately do NOT run here: a background is expected
             to be trimmed, and the die comes from the chosen template. */
          var ar = w / h;
          if (ar < AR_MIN || ar > AR_MAX) {
            H('shape', 'That image is ' + w + ' × ' + h + ' px (' + ar.toFixed(2)
              + ' : 1), which is the wrong shape for a label background — it would lose most '
              + 'of itself when cropped to ' + BOX_AR.toFixed(2) + ' : 1. Please choose an image '
              + 'closer to a landscape rectangle.');
            return out('');
          }

          /* ---- centre cover-crop to the ART BOX ratio (NOT the die ratio: the
             10 mL image tag bleeds 64 x 27 into a 63.5 x 25.4 viewBox, so its
             box ratio is 2.370, not 2.500). This is the same arithmetic as
             preserveAspectRatio="xMidYMid slice" and label_factory._fit_cover. */
          var cropW, cropH;
          if (ar > BOX_AR) { cropW = h * BOX_AR; cropH = h; }
          else             { cropW = w;          cropH = w / BOX_AR; }

          /* ---- GATE 3: DPI, measured on the CROPPED result. DPI_PASS / DPI_MIN
             are hardcoded on both surfaces on purpose. */
          cropDpi = Math.floor(Math.min(cropW / BOX_W_IN, cropH / BOX_H_IN));
          var needPass = Math.ceil(BOX_W_IN * DPI_PASS) + '×' + Math.ceil(BOX_H_IN * DPI_PASS);
          var needMin  = Math.ceil(BOX_W_IN * DPI_MIN)  + '×' + Math.ceil(BOX_H_IN * DPI_MIN);
          if (cropDpi >= DPI_PASS) {
            W('sharp', 'Sharp resolution — ' + cropDpi + ' DPI once it is fitted to the label.');
            warns[warns.length - 1].tier = 'pass';
          } else if (cropDpi >= DPI_MIN) {
            W('lowdpi', 'A little low-res (' + cropDpi + ' DPI at label size). It will print but may look '
              + 'slightly soft — ' + DPI_PASS + ' DPI or higher is best (about ' + needPass + ' px).');
            A('lowdpi', 'My background is low resolution and may print soft.',
                        'It is ' + cropDpi + ' DPI once fitted to the label. Fine lines lose detail.');
          } else {
            H('dpi', 'Too low-res to print clearly — only ' + cropDpi + ' DPI once fitted to the label '
              + '(we need at least ' + DPI_MIN + ' DPI, about ' + needMin + ' px). Please choose a larger image.');
          }

          /* ---- GATE 4(b): TRIM ACK. Name the edges and the percent, reusing the
             `offshape` wording from va-upload.js:1090-1101. The >1% guard is the
             same one that gates that message there. */
          var trimW = 1 - (cropW / w), trimH = 1 - (cropH / h);
          var trimFrac = Math.max(trimW, trimH);
          if (trimFrac > 0.01) {
            var trimPct = Math.round(trimFrac * 100);
            var trimWhere = (trimW > trimH) ? 'left and right sides' : 'top and bottom';
            W('trim', 'Your image is ' + ar.toFixed(2) + ' : 1 and the label background is '
              + BOX_AR.toFixed(2) + ' : 1. The image fills the label, so about ' + trimPct
              + '% will be trimmed off the ' + trimWhere + '.');
            A('trim', 'Trimming the ' + trimWhere + ' is OK.',
                      'About ' + trimPct + '% of the ' + trimWhere + ' will not print. Keep anything '
                      + 'you need to see away from those edges.');
          }

          /* ---- GATE 5: BLANK / TRANSPARENT (va-upload.js:1104-1119).
             BACKGROUNDS HARD-REJECT ALPHA. The old behaviour was a mandatory
             ack reading "these print as the material colour, not white" — and
             that promise was false on all three surfaces at once: the inline
             proof showed see-through (previewUrl keeps PNG alpha), the cart
             thumbnail showed WHITE (seedProof pre-fills #ffffff), and the
             press printed BLACK (PIL's RGBA->RGB drops the alpha band and
             keeps the hidden RGB, which every mainstream exporter writes as
             0,0,0 — that is #1086). A customer ticked a box agreeing to bare
             foil and got a solid black rectangle, and it survives operator
             review looking like an intentional dark design.
             A background is full-bleed art with no legitimate reason to carry
             alpha, so the honest fix is to refuse it and say how to fix it.
             _swap_bg_image now composites onto WHITE as the server-side
             backstop for a file that arrives some other way. mode:'label'
             keeps the warn+ack — bare foil IS the semantic there. */
          return blankScan(file).then(function (bs) {
            if (bs) {
              if (bs.allAlpha0) {
                H('empty', 'This image appears to be completely empty / transparent.');
                return out('');
              }
              if (bs.std < 2) {
                W('blank', 'This image looks blank or a single solid colour. Double-check you picked the right file.');
                A('blank', 'This is the background I want printed.',
                           'It looks blank or one solid colour — we print it exactly as supplied.');
              }
              if (bs.anyAlphaLt) {
                if (mode === 'background') {
                  H('transp', 'This image has see-through (transparent) areas. A background has to be '
                    + 'fully solid — please flatten it onto a solid colour and re-export it as a JPG, '
                    + 'or as a PNG with no transparency.');
                  return out('');
                }
                W('transp', 'Your image has transparent areas — these print as the material colour, not white.');
                A('transp', 'Transparent areas will show the bare material.',
                            'We have no white ink — see-through parts print as shiny foil, not white.');
              }
            }
            /* ---- GATE 6: FINE DETAIL. Non-blocking since 2026-07-17: mark it,
               ping the audit lane, let it through. A background covers the full
               label width, so that is the placed size. */
            return detailScan(file, box.w).then(function (loss) {
              var detailWarn = 0;
              if (loss >= DETAIL_LOSS) {
                detailWarn = Math.round(loss * 100) / 100;
                W('detail', 'Lots of very fine detail — hairline strokes may soften at label size.');
                if (!hard.length) {
                  /* SOFT audit entry, exactly as va-upload.js:1118 does */
                  auditReject(file, 'SOFT fine-detail ' + loss.toFixed(2)
                    + ' - allowed through (redesign upsell follow-up)', cropDpi, die);
                }
              }
              /* ---- GATE 7: LEGIBILITY under the reserved text zones. Warning
                 only. NEVER auto-recolour. */
              /* ZONES is the 3 mL furniture (label_factory.py:31 has no 10 mL
                 branch; the 10 mL templates carry their own absolute-mm
                 furniture), so on the 10 mL die a zone sample would grade
                 the wrong pixels. Skip rather than mis-warn. */
              var _zoneP = (die === '3ml') ? zoneLuma(file, BOX_AR)
                                           : Promise.resolve([]);
              return _zoneP.then(function (zl) {
                var bad = 0, i;
                for (i = 0; i < zl.length; i++) {
                  if (inkLum >= 128 ? (zl[i].lum > LUM_LIGHT) : (zl[i].lum < LUM_DARK)) bad++;
                }
                if (bad >= 2) {
                  W('contrast', inkLum >= 128
                    ? 'This background is very light behind the text. The label already outlines the '
                      + 'text, but a darker image prints more clearly.'
                    : 'This background is very dark behind the text. The label already outlines the '
                      + 'text, but a lighter image prints more clearly.');
                }
                if (hard.length) return out('');
                return previewUrl(file, dims).then(function (du) {
                  var r = out(du);
                  r.detailWarn = detailWarn;
                  r.contrastWarn = (bad >= 2) ? 1 : 0;
                  return r;
                });
              });
            });
          });
        }).catch(function () {
          H('unreadable', "We couldn't read that image. Please re-export it as a PNG or JPG and try again.");
          return out('');
        });
      });
    }).catch(function () {
      H('unreadable', "We couldn't read that image. Please re-export it as a PNG or JPG and try again.");
      return out('');
    });
  }

  /* ---------------------------------------------------------------------
     gate 8 — AI artwork scan, PRE-CART and BEFORE the R2 POST.
     Byte-for-byte the behaviour of vaScanArtwork in va-upload.js:1840-1856,
     plus kind=background so the Worker's SRC_FIELD labels the surface. Fails
     OPEN by design: a Worker blip must never block a customer. allowed===false
     throws an Error carrying .vaBlock for vaBlockToast('policy', sc).
     The Worker's sha24 verdict cache means Label Farm's ingest scan reuses this
     verdict with no second Anthropic call.
     --------------------------------------------------------------------- */
  function scan(file) {
    var base = String(window.VA_SCAN_URL || SCAN_BASE).replace(/\/artwork-scan\/?$/, '') + '/artwork-scan';
    var fd;
    try {
      fd = new FormData();
      fd.append('artwork', file);
      fd.append('kind', 'background');
    } catch (e) { return Promise.resolve(); }
    return fetch(base, { method: 'POST', body: fd })
      .then(function (r) { return r.ok ? r.json() : { allowed: true }; })
      .catch(function () { return { allowed: true }; })
      .then(function (sc) {
        if (sc && sc.allowed === false) {
          var err = new Error('VA_CONTENT_BLOCK');
          err.vaBlock = sc;
          throw err;
        }
        return sc || { allowed: true };
      });
  }

  /* ---------------------------------------------------------------------
     gate 10 — R2 storage. Raw bytes to the design Worker's bg lane. On ANY
     failure we STOP: there is deliberately NO fallback to a Shopify file
     upload. Shopify hosting the artwork is the leak this whole design removes.
     --------------------------------------------------------------------- */
  function upload(file, die) {
    var d = (die === '10ml') ? '10ml' : '3ml';
    var url = workerBase() + '/upload?bg=1&size=' + encodeURIComponent(d);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (j) {
          throw new Error((j && j.error) ? j.error : ('upload failed (' + r.status + ')'));
        }, function () { throw new Error('upload failed (' + r.status + ')'); });
      }
      return r.json();
    }).then(function (u) {
      if (!u || !u.id) throw new Error('upload returned no id');
      return u;
    });
  }

  window.vaArtCheck = {
    validate: validate,
    scan: scan,
    upload: upload,
    /* exposed so a caller can show the same numbers in its own copy */
    DPI_PASS: DPI_PASS, DPI_MIN: DPI_MIN, MAX_MB: MAX_MB,
    boxFor: boxFor
  };
})();
